import { Check, Tag as TagIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { Tag } from "@/features/settings/tags-api";
import { cn } from "@/lib/utils";

interface TagMultiSelectProps {
  /** Assignable tags to show, already ordered/filtered by the caller. */
  tags: Tag[];
  /** Currently-selected tag ids. */
  selected: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  /** Trigger label when nothing is selected. */
  placeholder?: string;
  className?: string;
  triggerSize?: "sm" | "default";
}

/**
 * Compact tag picker: a popover of grouped, checkable tags. Used both as the
 * Clients-list tag filter and the client-profile tag-assignment control.
 */
export function TagMultiSelect({
  tags,
  selected,
  onChange,
  disabled,
  placeholder = "Tags",
  className,
  triggerSize = "sm",
}: TagMultiSelectProps) {
  const selectedSet = new Set(selected);

  function toggle(id: string) {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next]);
  }

  // Group tags by category for readable sectioning.
  const byGroup = new Map<string, Tag[]>();
  for (const t of tags) {
    const list = byGroup.get(t.groupName) ?? [];
    list.push(t);
    byGroup.set(t.groupName, list);
  }

  const count = selected.length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size={triggerSize}
          disabled={disabled}
          className={cn("gap-1.5", className)}
        >
          <TagIcon className="size-4" />
          {count > 0 ? `${placeholder} (${count})` : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0">
        {tags.length === 0 ? (
          <p className="text-muted-foreground p-4 text-sm">No tags available.</p>
        ) : (
          <div className="max-h-72 overflow-y-auto p-1">
            {[...byGroup.entries()].map(([groupName, groupTags]) => (
              <div key={groupName} className="py-1">
                <p className="text-muted-foreground px-2 py-1 text-xs font-medium">
                  {groupName}
                </p>
                {groupTags.map((tag) => {
                  const isSelected = selectedSet.has(tag.id);
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => toggle(tag.id)}
                      className="hover:bg-muted flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm"
                    >
                      <span
                        className={cn(
                          "flex size-4 items-center justify-center rounded-sm border",
                          isSelected
                            ? "bg-primary border-primary text-primary-foreground"
                            : "border-input",
                        )}
                      >
                        {isSelected && <Check className="size-3" />}
                      </span>
                      <span className={tag.isArchived ? "text-muted-foreground" : undefined}>
                        {tag.name}
                        {tag.isArchived && " (archived)"}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}
        {count > 0 && (
          <div className="border-t p-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full justify-start"
              onClick={() => onChange([])}
            >
              Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

/** Read-only row of tag badges (e.g. on a client row / profile header). */
export function TagBadges({
  tags,
  className,
}: {
  tags: { id: string; name: string }[];
  className?: string;
}) {
  if (tags.length === 0) return null;
  return (
    <div className={cn("flex flex-wrap gap-1", className)}>
      {tags.map((t) => (
        <Badge key={t.id} variant="secondary" className="text-xs">
          {t.name}
        </Badge>
      ))}
    </div>
  );
}
